import type {
  DimensionColumnDataType,
  DimensionDataFormatType,
} from '@/entities/columnsConfig';
import type { OrganizationRole, ViewsRestrictions } from '@/entities/session';
import {
  baseApi,
  ERROR_CODES as API_ERROR_CODES,
  getErrorMessage,
} from '@/shared/api';
import { mapUser } from '../libs/mapUser';
import { ERROR_CODES as UI_ERROR_CODES } from '../model/constants';
import type { User, UserSettingsMeta } from '../model/types';
import type { RLSFilters, UserDTO } from './types';

export const getOrganizationUsers = async (): Promise<User[]> => {
  const { data, error } = await baseApi.functions.invoke<{
    users: UserDTO[];
  }>('getOrganizationUsers');

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data.users.map(mapUser);
};

export const inviteUserToOrganization = async (
  email: string,
  role: OrganizationRole,
): Promise<User> => {
  const { data, error } = await baseApi.functions.invoke<UserDTO>(
    'addOrganizationUser',
    {
      body: { user: { email, role } },
    },
  );

  if (!data || error) {
    let errorMessage: string = '';

    if (error.context.status === 422) {
      const errorContext = await error.context.json();

      if (errorContext.errorCode === API_ERROR_CODES.EMAIL_EXISTS) {
        // TODO: create custom error class
        // eslint-disable-next-line no-throw-literal
        throw {
          message: 'Email already registered.',
          code: UI_ERROR_CODES.EMAIL_EXISTS,
        };
      } else {
        errorMessage = errorContext.error;
      }
    }

    throw new Error(errorMessage || getErrorMessage(error));
  }

  return mapUser(data);
};

export const updateUser = async (updatedUser: {
  id: string;
  role?: OrganizationRole;
  isActive?: boolean;
  rlsFilters?: RLSFilters;
  clsFilters?: string[];
  viewsRestrictions?: ViewsRestrictions;
}): Promise<User> => {
  const { data, error } = await baseApi.functions.invoke<UserDTO>(
    'updateOrganizationUser',
    {
      body: {
        user: updatedUser,
      },
    },
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return mapUser(data);
};

export const getUserSettingsMeta = async (): Promise<UserSettingsMeta> => {
  const { data, error } = await baseApi.functions.invoke<{
    rlsColumns: string[];
    clsColumns: string[];
    config: Record<
      string,
      {
        name: string;
        description: string;
        group: string;
        dataType: DimensionDataFormatType;
        type: DimensionColumnDataType;
        order: number;
      }
    >;
  }>('getUserSettingsMeta');

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data;
};
